import React, { Component } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import theme, { tokens } from '../theme';
import Logo from '../admin/Logo';
import axios from 'axios';
import endpoints, { getBackendUrl, apiRequest } from '../endpoints';

import { setUserdata, setToken, login } from '../redux/authSlice'
import { store } from '../redux/store'
import { connect } from 'react-redux'
import Error from '../admin/pages/parts/Error';


class Login extends Component {
    state = {
        started: false,
        error: false,
        errorMessage: '',
        loginLink: '',
        googleLoginLink: '',
        localAuthEnabled: false,
        buttonEnabled: true,
        username: '',
        password: '',
        localLoginError: '',
        localLoginBusy: false,
    }

    async componentDidMount() {
        await this.getLoggedInState()
        setTimeout(() => {
            this.setState({
                ...this.state,
                started: true,
            })
        }, 500)
    }

    async getLoggedInState(tokenOverride) {
        try {
            let uri = getBackendUrl(endpoints.auth, tokenOverride || this.props.token)
            let res = await axios.get(uri)

            if (res.data.status === 'success') {
                this.loginIn(res.data.user)
            }
        } catch (e) {
            if ((e.response !== undefined) && (e.response.data.redirect)) {
                this.clearError()
                this.setState({
                    loginLink: e.response.data.redirect,
                    googleLoginLink: e.response.data.googleRedirect || '',
                    localAuthEnabled: !!e.response.data.localAuthEnabled,
                })
            } else {
                this.setError('No connection to the backend server. Reconnection is being attempted.')
                console.log('server error', e)
                
                setTimeout(() => {
                    this.getLoggedInState()
                }, 1000)
            }
        }
    }

    setError(message) {
        this.setState({
            error: true,
            errorMessage: message,
            buttonEnabled: false
        })
    }

    clearError() {
        this.setState({
            error: false,
            errorMessage: '',
            buttonEnabled: true
        })
    }
    
    setDisabledButton() {
        this.setState({
            ...this.state,
            buttonEnabled: false
        })
    }

    loginIn(userData) {
        store.dispatch(setUserdata(userData))
        store.dispatch(login())
    }

    handleLocalLoginSubmit = async (e) => {
        e.preventDefault()

        this.setState({
            localLoginError: '',
            localLoginBusy: true,
        })

        try {
            const res = await apiRequest('post', endpoints.auth_login, {
                username: this.state.username,
                password: this.state.password,
            }, false)

            if (res.data.status === 'success') {
                store.dispatch(setToken(res.data.token))
                await this.getLoggedInState(res.data.token)
            } else {
                this.setState({
                    localLoginError: res.data.message || 'Invalid credentials',
                })
            }
        } catch (e) {
            let message = 'No connection to the backend server.'
            if (e.response) {
                message = (e.response.data && e.response.data.message)
                    || `Server error (HTTP ${e.response.status})`
            }
            this.setState({
                localLoginError: message,
            })
        } finally {
            this.setState({
                localLoginBusy: false,
            })
        }
    }

    render() {
        const bgStyle = {
            minHeight: '100vh',
            display: 'flex',
            padding: 0,
            // Soft light canvas with two barely-there accent washes.
            background:
                `radial-gradient(60rem 40rem at 12% -10%, ${tokens.accentSoft} 0%, rgba(255,255,255,0) 60%),` +
                `radial-gradient(50rem 36rem at 100% 110%, rgba(18, 184, 134, 0.10) 0%, rgba(255,255,255,0) 60%),` +
                tokens.canvas,
        }

        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Container maxWidth={false} style={bgStyle}>
                    <Stack
                        sx={{ width: '100%', minHeight: '100vh' }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        {this.state.started ?
                        <Card
                            elevation={0}
                            sx={{
                                width: '100%',
                                maxWidth: 420,
                                borderRadius: '20px',
                                border: '1px solid ' + tokens.line,
                                boxShadow: tokens.shadowLg,
                            }}
                        >
                            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                                <Stack alignItems="center" spacing={1.25} sx={{ mb: 4 }}>
                                    <Box
                                        component="img"
                                        src="/gerda-wolf.png"
                                        alt=""
                                        sx={{ width: '100%', maxWidth: 260, height: 'auto', mb: 1 }}
                                    />
                                    <Logo size={38} mark={false} />
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Job feed intelligence &amp; AI cover letters
                                    </Typography>
                                </Stack>

                                <Error error={this.state.errorMessage} />

                                {this.state.error ? null :
                                <Stack spacing={2}>
                                    <Button
                                        href={this.state.loginLink}
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        fullWidth
                                        disabled={!this.state.buttonEnabled && this.state.loginLink !== ''}
                                        onClick={() => {
                                            this.setDisabledButton()
                                        }}
                                        sx={{ py: 1.25, fontSize: 15 }}
                                    >
                                        Sign in with Upwork
                                    </Button>
                                    <Typography variant="caption" color="text.disabled" align="center">
                                        You will be redirected to Upwork to authorise access.
                                    </Typography>

                                    {this.state.googleLoginLink &&
                                    <Button
                                        href={this.state.googleLoginLink}
                                        variant="outlined"
                                        color="primary"
                                        size="large"
                                        fullWidth
                                        disabled={!this.state.buttonEnabled}
                                        onClick={() => {
                                            this.setDisabledButton()
                                        }}
                                        sx={{ py: 1.25, fontSize: 15 }}
                                    >
                                        Sign in with Google
                                    </Button> }

                                    {this.state.localAuthEnabled &&
                                    <React.Fragment>
                                        <Divider>
                                            <Typography variant="caption" color="text.disabled">or</Typography>
                                        </Divider>
                                        <Stack
                                            component="form"
                                            onSubmit={this.handleLocalLoginSubmit}
                                            spacing={2}
                                        >
                                            <TextField
                                                label="Username"
                                                size="small"
                                                fullWidth
                                                value={this.state.username}
                                                onChange={(e) => this.setState({ username: e.target.value })}
                                            />
                                            <TextField
                                                label="Password"
                                                type="password"
                                                size="small"
                                                fullWidth
                                                value={this.state.password}
                                                onChange={(e) => this.setState({ password: e.target.value })}
                                            />
                                            <Error error={this.state.localLoginError} />
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                size="large"
                                                fullWidth
                                                disabled={this.state.localLoginBusy || !this.state.username || !this.state.password}
                                                sx={{ py: 1.25, fontSize: 15 }}
                                            >
                                                Sign in
                                            </Button>
                                        </Stack>
                                    </React.Fragment> }
                                </Stack> }
                            </CardContent>
                        </Card>
                        :
                        <Stack alignItems="center" spacing={3}>
                            <Logo size={34} />
                            <CircularProgress size={28} thickness={4} sx={{ color: tokens.accent }} />
                        </Stack> }
                    </Stack>
                </Container>
            </ThemeProvider>
        );
    }
}

function mapStateToProps(state) {
    return {
        token: state.auth.token,
        isLoggedIn: state.auth.isLoggedIn
    }
}

export default connect(mapStateToProps)(Login);